import { Box, Typography, Container, Chip, useTheme } from "@mui/material";
import { useTranslation } from "../../../../language";

const CATEGORY_GRADIENTS = {
  metabolism: "linear-gradient(160deg, #9BA6BA 0%, #5F687C 52%, #202331 100%)",
  immune:     "linear-gradient(160deg, #8793AA 0%, #4F586D 52%, #202331 100%)",
  organ:      "linear-gradient(160deg, #747F96 0%, #3F495F 52%, #202331 100%)",
  vitamins:   "linear-gradient(160deg, #626C82 0%, #343D52 52%, #202331 100%)",
};

function CategoryCard({ categoryKey, children }) {
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 4,
        overflow: "hidden",
        background: CATEGORY_GRADIENTS[categoryKey],
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse at 25% 20%, rgba(255,255,255,0.06) 0%, transparent 60%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box sx={{ position: "relative", zIndex: 1 }}>
        {children}
      </Box>
    </Box>
  );
}

export default function CoreBloodTestExplain() {

  const { t } = useTranslation();
  const theme = useTheme();

  const chipSx = {
    bgcolor: "rgba(255,255,255,0.13)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    border: "0.5px solid rgba(255,255,255,0.22)",
    color: "#fff",
    fontWeight: 400,
    fontSize: "11px",
    height: "18px",
    letterSpacing: "0.01em",
    "& .MuiChip-label": { px: 0.9 },
  };

  const categoryDetails = {
    metabolism: {
      titleKey: "track_core_1",
      markers: [
        t("marker_metabolism_1"),  t("marker_metabolism_2"),  t("marker_metabolism_3"),
        t("marker_metabolism_4"),  t("marker_metabolism_5"),  t("marker_metabolism_6"),
        t("marker_metabolism_7"),  t("marker_metabolism_8"),  t("marker_metabolism_9"),
        t("marker_metabolism_10"), t("marker_metabolism_11"), t("marker_metabolism_12"),
        t("marker_metabolism_13"),
      ],
    },
    immune: {
      titleKey: "track_core_2",
      markers: [
        t("marker_immune_1"),  t("marker_immune_2"),  t("marker_immune_3"),
        t("marker_immune_4"),  t("marker_immune_5"),  t("marker_immune_6"),
        t("marker_immune_7"),  t("marker_immune_8"),  t("marker_immune_9"),
        t("marker_immune_10"), t("marker_immune_11"), t("marker_immune_12"),
        t("marker_immune_13"), t("marker_immune_14"), t("marker_immune_15"),
        t("marker_immune_16"), t("marker_immune_17"), t("marker_immune_18"),
        t("marker_immune_19"), t("marker_immune_20"), t("marker_immune_21"),
        t("marker_immune_22"), t("marker_immune_23"), t("marker_immune_24"),
        t("marker_immune_25"), t("marker_immune_26"), t("marker_immune_27"),
      ],
    },
    organ: {
      titleKey: "track_core_3",
      markers: [
        t("marker_organ_1"),  t("marker_organ_2"),  t("marker_organ_3"),
        t("marker_organ_4"),  t("marker_organ_5"),  t("marker_organ_6"),
        t("marker_organ_7"),  t("marker_organ_8"),  t("marker_organ_9"),
        t("marker_organ_10"), t("marker_organ_11"), t("marker_organ_12"),
        t("marker_organ_13"), t("marker_organ_14"), t("marker_organ_15"),
        t("marker_organ_16"), t("marker_organ_17"), t("marker_organ_18"),
        t("marker_organ_19"),
      ],
    },
    vitamins: {
      titleKey: "track_core_4",
      markers: [
        t("marker_vitamins_1"),  t("marker_vitamins_2"),  t("marker_vitamins_3"),
        t("marker_vitamins_4"),  t("marker_vitamins_5"),  t("marker_vitamins_6"),
        t("marker_vitamins_7"),  t("marker_vitamins_8"),  t("marker_vitamins_9"),
        t("marker_vitamins_10"), t("marker_vitamins_11"), t("marker_vitamins_12"),
        t("marker_vitamins_13"), t("marker_vitamins_14"), t("marker_vitamins_15"),
        t("marker_vitamins_16"), t("marker_vitamins_17"),
      ],
    },
  };

  const categoryKeys = Object.keys(categoryDetails);

  return (
    <Box id="track-health-section" sx={{ textAlign: "center", overflowX: "hidden" }}>
      <Box sx={{ backgroundColor: "grey.050" }}>
        <Container maxWidth="md">

          {/* ── TITLE BLOCK ── */}
          <Box sx={{ textAlign: "center", mb: 6, mt: 4, px: { xs: 2, sm: 0 }, maxWidth: { xs: "100%", sm: 620, lg: 560, xl: 650 }, mx: "auto" }}>
            <Typography
              variant="h5"
              sx={{ color: "greydark.C900", letterSpacing: "-0.0001em", fontWeight: 400, mb: 0.2 }}
            >
              {t("track_start_with")}
            </Typography>
            <Typography variant="h2" sx={{ fontWeight: 400 }}>
              {t("track_slide_1_title")}
            </Typography>
            <Typography
              variant="h5"
              sx={{ mt: 0.1, mb: 4, color: "greydark.C900", fontWeight: 400, letterSpacing: "-0.0001em" }}
            >
              {t("track_blood_explanation2")}
            </Typography>
            <Typography
              variant="h5"
              sx={{ color: "greydark.C900", letterSpacing: "-0.0001em", fontWeight: 400, maxWidth: { xs: "90%", sm: "520px" }, margin: "0 auto" }}
            >
              {t("track_blood_explanation")}
              {t("track_blood_explanation2")}
              {t("track_blood_explanation3")}
            </Typography>
          </Box>

          {/* ── CARD GRID ── */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: { xs: 1, md: 2 },
              maxWidth: { xs: "100%", sm: 620, lg: 560, xl: 650 },
              mx: "auto",
              pb: 4,
            }}
          >
            {categoryKeys.map((key) => {
              const cat = categoryDetails[key];
              return (
                <CategoryCard key={key} categoryKey={key}>
                  <Box sx={{ display: "flex", flexDirection: "column", pt: { xs: 2, sm: 2.5, md: 3 }, px: { xs: 2, sm: 2.5, md: 3 }, pb: { xs: 3, sm: 4, md: 5 } }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                      <Typography
                        sx={{
                          color: "#fff",
                          fontWeight: 500,
                          lineHeight: 1.2,
                          letterSpacing: "-0.01em",
                          fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                        }}
                      >
                        {t(cat.titleKey)}
                      </Typography>
                      <Chip
                        label={cat.markers.length}
                        size="small"
                        sx={{
                          bgcolor: theme.palette.primary.C300,
                          border: `0.5px solid ${theme.palette.primary.C400}`,
                          color: "rgba(0,0,0,0.65)",
                          fontWeight: 600,
                          fontSize: { xs: "0.85rem", sm: "0.9rem" },
                          height: 24,
                          flexShrink: 0,
                          ml: 1,
                          "& .MuiChip-label": { px: 1.2 },
                        }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {cat.markers.map((marker, idx) => (
                        <Chip key={idx} label={marker} size="small" sx={chipSx} />
                      ))}
                    </Box>
                  </Box>
                </CategoryCard>
              );
            })}
          </Box>

        </Container>
      </Box>
    </Box>
  );
}
